import { useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import authService from '../services/authService';
import useAuthStore from '../stores/authStore';
import { API_BASE_URL } from '../constants/apiBase';
import { apiFetch } from '../services/apiFetch';

// BroadcastChannel to notify other tabs when a token is refreshed.
// Deduplication of concurrent refresh calls is handled by authService.refreshJwtToken().
const refreshChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('craftflow_token_refresh') : null;

if (refreshChannel) {
  refreshChannel.onmessage = event => {
    if (event.data?.type === 'TOKEN_REFRESHED' && event.data.token) {
      // Another tab refreshed the token — update our store
      useAuthStore.getState().setUserToken(event.data.token);
    }
  };
}

// X-CSRF-Token is added by apiFetch (services/apiFetch.js) — the one CSRF rule
// for the whole admin. This file used to keep a private copy of it that only
// apiCall used; fetchFile and authService had none, so their POSTs 403'd
// whenever the browser sent the access_token cookie.

// Standalone API call function for use in AuthContext (no circular dependency)
export const apiCall = async (url, options = {}, token = null) => {
  // Construct the full URL using the API base URL
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  const headers = {
    ...options.headers,
  };

  // Only set Content-Type to application/json if not already set, and not for FormData or URLSearchParams
  if (!(options.body instanceof FormData) && !(options.body instanceof URLSearchParams) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response;
  try {
    response = await apiFetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include',
      // Only add timeout if specifically requested (uploads should have no timeout)
      signal: options.signal || (options.timeout ? AbortSignal.timeout(options.timeout) : undefined),
    });
  } catch (networkError) {
    throw networkError;
  }

  if (!response.ok) {
    // Get the response as text first to see if it's HTML
    const responseText = await response.text();

    // Try to parse as JSON, fallback to text error
    try {
      const errorData = JSON.parse(responseText);
      // `detail` has FOUR shapes:
      //   - a string (plain HTTPException)
      //   - an object ({error_code, message, context}) for domain errors
      //   - an ARRAY of {loc, msg, type} — FastAPI's stock 422 validation errors
      //   - the app's OWN 422 envelope (server/src/message.py log_and_return_422): an object
      //     {error_code: 'VALIDATION.FAILED', message: 'Request validation failed', errors: [{loc, msg, type}]}
      // The array fell through `detail?.message` to undefined, so every validation failure
      // surfaced as "HTTP error! status: 422" with the actual cause discarded; the envelope
      // surfaced as the bare sentence "Request validation failed" for the same reason. Callers
      // can now read `fieldErrors` to put the message against the field that caused it. This is
      // the shape the LIVE server answers with, so it is the one that matters.
      const detail = errorData.detail;
      const isValidationEnvelope =
        Boolean(detail) &&
        typeof detail === 'object' &&
        !Array.isArray(detail) &&
        detail.error_code === 'VALIDATION.FAILED' &&
        Array.isArray(detail.errors) &&
        detail.errors.length > 0;
      const validationItems = Array.isArray(detail) ? detail : isValidationEnvelope ? detail.errors : null;
      const fieldErrors = {};
      if (validationItems) {
        for (const item of validationItems) {
          // loc is like ["body", "price"] or ["body", "variants", 0, "sku"];
          // drop the "body"/"query" root so the key matches the form field.
          const path = (item?.loc || []).filter(p => p !== 'body' && p !== 'query');
          const key = path.length ? path.join('.') : '_';
          if (!fieldErrors[key]) fieldErrors[key] = item?.msg || 'Invalid value';
        }
      }
      const detailMsg =
        typeof detail === 'string'
          ? detail
          : validationItems
            ? validationItems
                .map(item => {
                  const path = (item?.loc || []).filter(p => p !== 'body' && p !== 'query');
                  return path.length ? `${path.join('.')}: ${item?.msg}` : item?.msg;
                })
                .filter(Boolean)
                .join('; ') || `HTTP error! status: ${response.status}`
            : detail?.message || errorData.message || `HTTP error! status: ${response.status}`;
      const error = new Error(detailMsg);
      error.status = response.status;
      // An array has no code; the envelope keeps its own (it always did).
      error.errorCode = typeof detail === 'object' && !Array.isArray(detail) ? detail?.error_code : undefined;
      if (validationItems) {
        error.fieldErrors = fieldErrors;
        error.validationErrors = validationItems;
      }
      // Attach structured failure details if present (e.g. from Etsy upload responses)
      if (errorData.failed_details) {
        error.failedDetails = errorData.failed_details;
      }
      // Extract entitlement context fields for upgrade prompts
      if (typeof detail === 'object' && detail?.context) {
        // Surface the raw structured context so callers can read arbitrary
        // fields (e.g. shop-reset 409's existing_job_id / existing_status).
        error.context = detail.context;
        error.requiredTier = detail.context.required_tier;
        error.feature = detail.context.feature;
        error.currentPlan = detail.context.current_plan;
      }
      throw error;
    } catch (jsonError) {
      if (jsonError.status) throw jsonError;
      const error = new Error(`HTTP ${response.status}: Received HTML instead of JSON`);
      error.status = response.status;
      throw error;
    }
  }

  // Handle 204 No Content responses (common for DELETE requests)
  if (response.status === 204) {
    return { success: true };
  }

  // Also add error handling for the success case
  const responseText = await response.text();

  // Handle empty responses
  if (!responseText || responseText.trim() === '') {
    return { success: true };
  }

  try {
    return JSON.parse(responseText);
  } catch (jsonError) {
    console.error('JSON Parse Error:', {
      url,
      status: response.status,
      responseText: responseText.substring(0, 200),
    });
    throw new Error('Received HTML instead of JSON from API');
  }
};

export const useApi = () => {
  const { userToken, logout } = useAuth();

  // Read through refs so the ONE api object below can outlive a token refresh.
  // A ref is also strictly more correct than the closure it replaces: it always
  // holds the latest value, where a closure pinned whatever existed when it was
  // built — the very bug `useApiTokenFreshness.test.js` was written for.
  const tokenRef = useRef(userToken);
  tokenRef.current = userToken;
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // `options.retryOn401` (default true) is a per-call opt-out from the
  // refresh-and-replay that follows a 401. It is NOT a fetch option, so it is
  // stripped before the request goes out. Set it to false on a non-idempotent
  // POST whose replay would be a duplicate side effect (the ODP push call sites).
  const authenticatedApiCall = async (url, rawOptions = {}, isRetry = false) => {
    const { retryOn401 = true, ...options } = rawOptions;
    // ALWAYS read the live store, never the render-time `userToken`.
    //
    // This used to be `isRetry ? store : userToken`, so only the retry saw a
    // refreshed token. Long-lived callers capture `api` once — e.g.
    // `useUnreadMessagesCount` builds its poller inside a `useEffect([enabled])`
    // — so their closure pinned whatever token existed at that render and kept
    // sending it after every refresh. Measured in production: a 401 →
    // refresh → retry cycle on BOTH 30s pollers, every 30s, for the whole
    // session, against a 120-minute token. It always recovered (hence "just
    // noise"), at the cost of doubling every poll and logging a JWT error each
    // time. `userToken` remains the fallback for the first call of a fresh
    // mount, before the store is populated.
    const currentToken = useAuthStore.getState().userToken || tokenRef.current;

    try {
      return await apiCall(url, options, currentToken);
    } catch (error) {
      // 401 detection MUST come from error.status, and from nothing else.
      // The server returns `{"detail": "Could not verify authentication token"}`
      // for an expired JWT — there's no "401" anywhere in that string, so a
      // message check silently misses it. apiCall sets `status` on EVERY HTTP
      // error (JSON and non-JSON bodies alike), so status is sufficient. There
      // is deliberately NO message-substring fallback: `message.includes('401')`
      // matched any error text containing those digits (an order id, a 502
      // whose body said "401") and logged the seller out of a healthy session
      // (#850).
      const is401 = error.status === 401;
      if (is401 && !retryOn401) {
        // Opt-out (ODP push): never replay the POST — a replay after refresh
        // could submit the print job twice — and never log out from here. Still
        // refresh the token, best-effort, so the seller's next click works, and
        // surface the failure to the caller.
        try {
          const refreshResult = await authService.refreshJwtToken();
          if (refreshResult?.success && refreshChannel) {
            refreshChannel.postMessage({
              type: 'TOKEN_REFRESHED',
              token: useAuthStore.getState().userToken,
            });
          }
        } catch (refreshError) {
          console.error('Token refresh error:', refreshError);
        }
        throw error;
      }
      if (is401) {
        // Don't retry if this is already a retry attempt
        if (isRetry) {
          logoutRef.current();
          window.location.href = '/login?expired=1';
          throw new Error('Session expired. Please log in again.');
        }

        try {
          const refreshResult = await authService.refreshJwtToken();

          if (refreshResult.success) {
            // Notify other tabs about the refreshed token
            if (refreshChannel) {
              refreshChannel.postMessage({
                type: 'TOKEN_REFRESHED',
                token: useAuthStore.getState().userToken,
              });
            }
            // Retry the original request with the new token
            return await authenticatedApiCall(url, rawOptions, true);
          } else {
            logoutRef.current();
            window.location.href = '/login?expired=1';
            throw new Error('Session expired. Please log in again.');
          }
        } catch (refreshError) {
          console.error('Token refresh error:', refreshError);
          logoutRef.current();
          window.location.href = '/login?expired=1';
          throw new Error('Session expired. Please log in again.');
        }
      }
      // Handle 402 Payment Required (limit exceeded, trial expired, or account suspended)
      if (error.status === 402) {
        const errorCode = error.errorCode || '';
        if (errorCode === 'SUBSCRIPTION.ACCOUNT_SUSPENDED') {
          window.location.href = '/account-suspended';
          return;
        }
        // Trial expired or subscription required — refresh subscription state
        if (errorCode === 'SUBSCRIPTION.TRIAL_EXPIRED' || errorCode === 'SUBSCRIPTION.REQUIRED') {
          try {
            const { default: useSubscriptionStore } = await import('../stores/subscriptionStore');
            useSubscriptionStore.getState().fetchSubscription();
          } catch (_e) {
            // Best-effort refresh
          }
        }
        const upgradeDetail = {
          code: errorCode || 'LIMIT_EXCEEDED',
          message: error.message,
          required_tier: error.requiredTier,
          feature: error.feature,
          current_plan: error.currentPlan,
        };
        window.dispatchEvent(new CustomEvent('craftflow:upgrade-needed', { detail: upgradeDetail }));
      }

      // Handle 403 with PLAN.ACCESS_DENIED error code
      if (error.status === 403 && error.errorCode === 'PLAN.ACCESS_DENIED') {
        const upgradeDetail = {
          code: error.errorCode,
          message: error.message,
          required_tier: error.requiredTier,
          feature: error.feature,
          current_plan: error.currentPlan,
        };
        window.dispatchEvent(new CustomEvent('craftflow:upgrade-needed', { detail: upgradeDetail }));
      }

      throw error;
    }
  };

  const get = (url, options = {}) => authenticatedApiCall(url, { method: 'GET', ...options });
  const post = (url, data, options = {}) =>
    authenticatedApiCall(url, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  const postFormData = (url, formData, options = {}) => {
    // Remove timeout for uploads - let them run as long as needed
    const { timeout, ...restOptions } = options;
    return authenticatedApiCall(url, {
      method: 'POST',
      body: formData,
      ...restOptions,
    });
  };

  // Enhanced form data post with retry logic for connection reset errors
  const postFormDataWithRetry = async (url, formData, maxRetries = 2) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await postFormData(url, formData);
        return result;
      } catch (error) {
        // Check if it's a retryable error (network issues, not timeouts since we removed them)
        const isRetryableError =
          error.message.includes('ERR_CONNECTION_RESET') ||
          error.message.includes('Failed to fetch') ||
          error.name === 'TypeError';

        // If it's the last attempt or not a retryable error, throw
        if (attempt === maxRetries || !isRetryableError) {
          throw error;
        }

        // Wait before retrying (exponential backoff)
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };
  const put = (url, data, options = {}) =>
    authenticatedApiCall(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  const patch = (url, data, options = {}) =>
    authenticatedApiCall(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    });
  const putFormData = (url, formData, options = {}) =>
    authenticatedApiCall(url, {
      method: 'PUT',
      body: formData,
      ...options,
    });
  const del = (url, options = {}) => authenticatedApiCall(url, { method: 'DELETE', ...options });

  // Raw fetch for downloading files (returns response, not JSON)
  const fetchFile = async (url, options = {}, isRetry = false) => {
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    // Same live-store read as authenticatedApiCall — see the note there. This
    // sibling carried the identical stale-closure defect; fixing only one of
    // them would leave file downloads paying the 401-refresh-retry tax.
    const currentToken = useAuthStore.getState().userToken || tokenRef.current;

    const headers = {
      ...options.headers,
    };

    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    const response = await apiFetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      // Handle 401 with token refresh
      if (response.status === 401 && !isRetry) {
        try {
          const refreshResult = await authService.refreshJwtToken();
          if (refreshResult.success) {
            return await fetchFile(url, options, true);
          }
        } catch (refreshError) {
          console.error('Token refresh error:', refreshError);
        }

        logoutRef.current();
        window.location.href = '/login?expired=1';
        throw new Error('Session expired. Please log in again.');
      }

      const contentType = response.headers.get('content-type');
      let fileError;
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        const detail = errorData.detail;
        const message = typeof detail === 'string' ? detail : detail?.message;
        fileError = new Error(message || `HTTP error! status: ${response.status}`);
      } else {
        const errorText = await response.text();
        fileError = new Error(`HTTP ${response.status}: ${errorText.substring(0, 200)}`);
      }
      fileError.status = response.status;
      throw fileError;
    }

    return response;
  };

  // ONE object for the life of the component. It used to be a fresh literal on
  // every render, which made `api` unusable as a dependency: every consumer that
  // listed it rebuilt its whole callback graph each render, so the `useCallback`
  // wrappers throughout the Shopify hooks and pages were decorative. Built from
  // the first render's closures, which is safe because the only render-scoped
  // values they touch — the token and `logout` — are read through refs above.
  const apiRef = useRef(null);
  if (apiRef.current === null) {
    apiRef.current = {
      get,
      post,
      postFormData,
      postFormDataWithRetry,
      put,
      patch,
      putFormData,
      delete: del,
      fetchFile,
      baseUrl: API_BASE_URL,
    };
  }
  return apiRef.current;
};
